import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { FileDown, RefreshCw, AlertCircle, Sparkles, TrendingUp, ShieldAlert, Lightbulb } from "lucide-react";
import { useBusinesses } from "@/hooks/use-businesses";
import { Sidebar } from "@/components/ui/sidebar";

type PLAccount = {
  accountId: number;
  code: string;
  name: string;
  subtype: string | null;
  amount: number;
  transactionCount: number;
};
type PLGroup = {
  subtype: string;
  subtypeLabel: string;
  accounts: PLAccount[];
  total: number;
};
type PLSection = {
  groups: PLGroup[];
  accounts: PLAccount[];
  total: number;
};
type PLTotals = {
  revenue: number;
  cogs: number;
  grossProfit: number;
  expenses: number;
  otherIncome: number;
  otherExpenses: number;
  netIncome: number;
};
type ProfitLossResult = {
  period: { startDate: string; endDate: string };
  revenue: PLSection;
  cogs: PLSection;
  expenses: PLSection;
  otherIncome: PLSection;
  otherExpenses: PLSection;
  totals: PLTotals;
  comparison?: {
    period: { startDate: string; endDate: string };
    revenue: PLSection;
    cogs: PLSection;
    expenses: PLSection;
    otherIncome: PLSection;
    otherExpenses: PLSection;
    totals: PLTotals;
  };
  validation: {
    journalBalanced: boolean;
    debitTotal: number;
    creditTotal: number;
  };
};

const fmtMoney = (n: number): string => {
  const abs = Math.abs(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `($${abs})` : `$${abs}`;
};

const today = () => new Date().toISOString().slice(0, 10);
const startOfYear = () => {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
};
const startOfPriorYear = () => {
  const d = new Date();
  return new Date(d.getFullYear() - 1, 0, 1).toISOString().slice(0, 10);
};
const endOfPriorYear = () => {
  const d = new Date();
  return new Date(d.getFullYear() - 1, 11, 31).toISOString().slice(0, 10);
};

function SummaryTable({ data }: { data: ProfitLossResult }) {
  return (
    <div className="divide-y">
      <Section title="Revenue" accounts={data.revenue.accounts} total={data.totals.revenue} />
      {data.cogs.accounts.length > 0 && (
        <>
          <Section title="Cost of Goods Sold" accounts={data.cogs.accounts} total={data.totals.cogs} />
          <TotalRow label="Gross Profit" amount={data.totals.grossProfit} emphasis />
        </>
      )}
      <Section title="Expenses" accounts={data.expenses.accounts} total={data.totals.expenses} />
      {data.otherIncome.accounts.length > 0 && (
        <Section title="Other Income" accounts={data.otherIncome.accounts} total={data.totals.otherIncome} />
      )}
      {data.otherExpenses.accounts.length > 0 && (
        <Section title="Other Expenses" accounts={data.otherExpenses.accounts} total={data.totals.otherExpenses} />
      )}
      <div className="pt-4">
        <div className="flex justify-between text-lg font-bold border-t-2 border-double border-black pt-2">
          <span>Net Income</span>
          <span className={data.totals.netIncome >= 0 ? "text-green-700" : "text-red-700"}>
            {fmtMoney(data.totals.netIncome)}
          </span>
        </div>
      </div>
    </div>
  );
}

function Section({ title, accounts, total }: { title: string; accounts: PLAccount[]; total: number }) {
  return (
    <div className="py-3">
      <h3 className="font-semibold text-sm uppercase tracking-wide text-gray-700 mb-2">{title}</h3>
      <div className="space-y-1 ml-4">
        {accounts.length === 0 ? (
          <div className="text-sm text-muted-foreground">No activity</div>
        ) : (
          accounts.map((a) => (
            <div key={a.accountId} className="flex justify-between text-sm">
              <span className="text-gray-700">
                <span className="text-gray-400 mr-2">{a.code}</span>
                {a.name}
              </span>
              <span className="tabular-nums">{fmtMoney(a.amount)}</span>
            </div>
          ))
        )}
      </div>
      <div className="flex justify-between font-semibold mt-2 pt-2 border-t">
        <span>Total {title}</span>
        <span className="tabular-nums">{fmtMoney(total)}</span>
      </div>
    </div>
  );
}

function TotalRow({ label, amount, emphasis = false }: { label: string; amount: number; emphasis?: boolean }) {
  return (
    <div className={`flex justify-between py-3 ${emphasis ? "font-bold border-y-2 border-black" : "font-semibold"}`}>
      <span>{label}</span>
      <span className="tabular-nums">{fmtMoney(amount)}</span>
    </div>
  );
}

function DetailedTable({ data }: { data: ProfitLossResult }) {
  const renderSection = (title: string, section: PLSection) => {
    if (section.accounts.length === 0) return null;
    return (
      <div className="py-4">
        <h3 className="font-bold text-sm uppercase tracking-wide text-gray-800 mb-2">{title}</h3>
        {section.groups.map((g) => (
          <div key={g.subtype} className="mb-3">
            {(g.accounts.length > 1 || g.subtypeLabel !== "Other") && (
              <div className="ml-4 font-semibold text-sm text-gray-700">{g.subtypeLabel}</div>
            )}
            <div className="ml-8 space-y-1">
              {g.accounts.map((a) => (
                <div key={a.accountId} className="flex justify-between text-sm">
                  <span>
                    <span className="text-gray-400 mr-2">{a.code}</span>
                    {a.name}
                  </span>
                  <span className="tabular-nums">{fmtMoney(a.amount)}</span>
                </div>
              ))}
            </div>
            {g.accounts.length > 1 && (
              <div className="ml-4 flex justify-between text-sm font-semibold border-t mt-1 pt-1">
                <span>Subtotal — {g.subtypeLabel}</span>
                <span className="tabular-nums">{fmtMoney(g.total)}</span>
              </div>
            )}
          </div>
        ))}
        <div className="flex justify-between font-bold border-t pt-2">
          <span>Total {title}</span>
          <span className="tabular-nums">{fmtMoney(section.total)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="divide-y">
      {renderSection("Revenue", data.revenue)}
      {data.cogs.accounts.length > 0 && (
        <>
          {renderSection("Cost of Goods Sold", data.cogs)}
          <TotalRow label="Gross Profit" amount={data.totals.grossProfit} emphasis />
        </>
      )}
      {renderSection("Expenses", data.expenses)}
      {renderSection("Other Income", data.otherIncome)}
      {renderSection("Other Expenses", data.otherExpenses)}
      <div className="pt-4">
        <div className="flex justify-between text-lg font-bold border-t-2 border-double border-black pt-2">
          <span>Net Income</span>
          <span className={data.totals.netIncome >= 0 ? "text-green-700" : "text-red-700"}>
            {fmtMoney(data.totals.netIncome)}
          </span>
        </div>
      </div>
    </div>
  );
}

function ComparisonTable({ data }: { data: ProfitLossResult }) {
  if (!data.comparison) return <div className="text-muted-foreground">No comparison period selected.</div>;
  const cur = data.totals;
  const prev = data.comparison.totals;

  const row = (label: string, a: number, b: number, opts: { bold?: boolean; emphasis?: boolean } = {}) => (
    <div className={`grid grid-cols-[1fr_auto_auto] gap-8 py-1.5 items-center ${opts.bold ? "font-semibold border-t pt-2" : ""} ${opts.emphasis ? "font-bold border-y-2 border-black py-2" : ""}`}>
      <span>{label}</span>
      <span className="tabular-nums text-right min-w-[120px]">{fmtMoney(a)}</span>
      <span className="tabular-nums text-right min-w-[120px] text-gray-600">{fmtMoney(b)}</span>
    </div>
  );

  const accountRows = (cur: PLAccount[], prev: PLAccount[]) => {
    const map = new Map<string, { name: string; cur: number; prev: number; code: string }>();
    cur.forEach((a) => map.set(a.code, { name: a.name, cur: a.amount, prev: 0, code: a.code }));
    prev.forEach((a) => {
      const x = map.get(a.code) || { name: a.name, cur: 0, prev: 0, code: a.code };
      x.prev = a.amount;
      map.set(a.code, x);
    });
    return Array.from(map.values()).sort((a, b) => a.code.localeCompare(b.code));
  };

  return (
    <div>
      <div className="grid grid-cols-[1fr_auto_auto] gap-8 pb-2 border-b font-bold text-sm">
        <span></span>
        <span className="text-right min-w-[120px]">
          {data.period.startDate} – {data.period.endDate}
        </span>
        <span className="text-right min-w-[120px]">
          {data.comparison.period.startDate} – {data.comparison.period.endDate}
        </span>
      </div>

      <div className="py-3">
        <h3 className="font-semibold text-sm uppercase text-gray-700 mb-1">Revenue</h3>
        {accountRows(data.revenue.accounts, data.comparison.revenue.accounts).map((r) => (
          <div key={r.code} className="grid grid-cols-[1fr_auto_auto] gap-8 py-0.5 text-sm ml-4">
            <span>
              <span className="text-gray-400 mr-2">{r.code}</span>
              {r.name}
            </span>
            <span className="tabular-nums text-right min-w-[120px]">{fmtMoney(r.cur)}</span>
            <span className="tabular-nums text-right min-w-[120px] text-gray-600">{fmtMoney(r.prev)}</span>
          </div>
        ))}
        {row("Total Revenue", cur.revenue, prev.revenue, { bold: true })}
      </div>

      {(data.cogs.accounts.length > 0 || data.comparison.cogs.accounts.length > 0) && (
        <div className="py-3">
          <h3 className="font-semibold text-sm uppercase text-gray-700 mb-1">Cost of Goods Sold</h3>
          {accountRows(data.cogs.accounts, data.comparison.cogs.accounts).map((r) => (
            <div key={r.code} className="grid grid-cols-[1fr_auto_auto] gap-8 py-0.5 text-sm ml-4">
              <span>
                <span className="text-gray-400 mr-2">{r.code}</span>
                {r.name}
              </span>
              <span className="tabular-nums text-right min-w-[120px]">{fmtMoney(r.cur)}</span>
              <span className="tabular-nums text-right min-w-[120px] text-gray-600">{fmtMoney(r.prev)}</span>
            </div>
          ))}
          {row("Total COGS", cur.cogs, prev.cogs, { bold: true })}
          {row("Gross Profit", cur.grossProfit, prev.grossProfit, { emphasis: true })}
        </div>
      )}

      <div className="py-3">
        <h3 className="font-semibold text-sm uppercase text-gray-700 mb-1">Expenses</h3>
        {accountRows(data.expenses.accounts, data.comparison.expenses.accounts).map((r) => (
          <div key={r.code} className="grid grid-cols-[1fr_auto_auto] gap-8 py-0.5 text-sm ml-4">
            <span>
              <span className="text-gray-400 mr-2">{r.code}</span>
              {r.name}
            </span>
            <span className="tabular-nums text-right min-w-[120px]">{fmtMoney(r.cur)}</span>
            <span className="tabular-nums text-right min-w-[120px] text-gray-600">{fmtMoney(r.prev)}</span>
          </div>
        ))}
        {row("Total Expenses", cur.expenses, prev.expenses, { bold: true })}
      </div>

      {(cur.otherIncome !== 0 || prev.otherIncome !== 0) && row("Total Other Income", cur.otherIncome, prev.otherIncome, { bold: true })}
      {(cur.otherExpenses !== 0 || prev.otherExpenses !== 0) && row("Total Other Expenses", cur.otherExpenses, prev.otherExpenses, { bold: true })}

      <div className="mt-4 pt-2 border-t-2 border-double border-black">
        <div className="grid grid-cols-[1fr_auto_auto] gap-8 py-2 text-lg font-bold">
          <span>Net Income</span>
          <span className={`tabular-nums text-right min-w-[120px] ${cur.netIncome >= 0 ? "text-green-700" : "text-red-700"}`}>
            {fmtMoney(cur.netIncome)}
          </span>
          <span className={`tabular-nums text-right min-w-[120px] ${prev.netIncome >= 0 ? "text-green-700" : "text-red-700"}`}>
            {fmtMoney(prev.netIncome)}
          </span>
        </div>
      </div>
    </div>
  );
}

type InsightsResponse = {
  period: { startDate: string; endDate: string };
  comparePeriod: { startDate: string; endDate: string } | null;
  insights: {
    summary: string[];
    risks: string[];
    opportunities: string[];
    metrics: {
      revenueGrowth: number | null;
      expenseGrowth: number | null;
      netIncomeGrowth: number | null;
      marginCurrent: number | null;
      marginPrevious: number | null;
      marginChange: number | null;
      expenseConcentration: number | null;
      revenueConcentration: number | null;
      topExpenseShare: number | null;
      topExpenseName: string | null;
    };
    topExpenses: { name: string; amount: number; share: number }[];
    topRevenue: { name: string; amount: number; share: number }[];
  };
  narrative: string | null;
};

function InsightsPanel({ data }: { data: InsightsResponse }) {
  const { insights } = data;
  const hasAny =
    insights.summary.length > 0 || insights.risks.length > 0 || insights.opportunities.length > 0;

  if (!hasAny) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-4 h-4" />
            AI Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No significant trends detected for this period.
          </p>
        </CardContent>
      </Card>
    );
  }

  const pct = (f: number | null) =>
    f === null || !isFinite(f) ? "—" : `${(f * 100).toFixed(1)}%`;
  const grow = (n: number | null) =>
    n === null || !isFinite(n) ? "—" : `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="w-4 h-4" />
          AI Insights
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricTile label="Revenue Growth" value={grow(insights.metrics.revenueGrowth)} />
          <MetricTile label="Expense Growth" value={grow(insights.metrics.expenseGrowth)} />
          <MetricTile label="Net Income Growth" value={grow(insights.metrics.netIncomeGrowth)} />
          <MetricTile
            label="Profit Margin"
            value={pct(insights.metrics.marginCurrent)}
            sub={
              insights.metrics.marginPrevious !== null
                ? `was ${pct(insights.metrics.marginPrevious)}`
                : undefined
            }
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <InsightCard
            icon={<TrendingUp className="w-4 h-4 text-green-700" />}
            title="Key Highlights"
            items={insights.summary}
            emptyText="No notable highlights."
          />
          <InsightCard
            icon={<ShieldAlert className="w-4 h-4 text-red-700" />}
            title="Risks"
            items={insights.risks}
            emptyText="No risks flagged."
          />
          <InsightCard
            icon={<Lightbulb className="w-4 h-4 text-amber-600" />}
            title="Opportunities"
            items={insights.opportunities}
            emptyText="No opportunities identified."
          />
        </div>

        {insights.topExpenses.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Top Expense Drivers</h4>
            <div className="space-y-1">
              {insights.topExpenses.map((e) => (
                <div key={e.name} className="flex justify-between text-sm">
                  <span>{e.name}</span>
                  <span className="tabular-nums text-gray-600">
                    {`$${e.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    <span className="ml-2 text-gray-400">({pct(e.share)})</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.narrative && (
          <div className="pt-2 border-t">
            <h4 className="text-sm font-semibold text-gray-700 mb-1">Narrative</h4>
            <p className="text-sm text-gray-800 leading-relaxed">{data.narrative}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MetricTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="border rounded-md p-3 bg-white">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

function InsightCard({
  icon, title, items, emptyText,
}: { icon: React.ReactNode; title: string; items: string[]; emptyText: string }) {
  return (
    <div className="border rounded-md p-3 bg-white">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h4 className="text-sm font-semibold">{title}</h4>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyText}</p>
      ) : (
        <ul className="space-y-1.5 text-sm list-disc list-inside text-gray-700">
          {items.map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ProfitLossV2Page() {
  const { activeBusiness } = useBusinesses();
  const [tab, setTab] = useState<"summary" | "comparison" | "detailed">("summary");
  const [startDate, setStartDate] = useState(startOfYear());
  const [endDate, setEndDate] = useState(today());
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareStartDate, setCompareStartDate] = useState(startOfPriorYear());
  const [compareEndDate, setCompareEndDate] = useState(endOfPriorYear());

  const useComparisonQuery = tab === "comparison" || compareEnabled;

  const query = useMemo(() => {
    const params = new URLSearchParams({ startDate, endDate });
    if (useComparisonQuery) {
      params.set("compareStartDate", compareStartDate);
      params.set("compareEndDate", compareEndDate);
    }
    return params.toString();
  }, [startDate, endDate, compareStartDate, compareEndDate, useComparisonQuery]);

  const endpoint =
    tab === "comparison"
      ? "/api/reports/pl/comparison"
      : tab === "detailed"
      ? "/api/reports/pl/detailed"
      : "/api/reports/pl/summary";

  const { data, isLoading, isError, refetch, isFetching } = useQuery<ProfitLossResult>({
    queryKey: [endpoint, activeBusiness?.id, query],
    queryFn: async () => {
      const res = await fetch(`${endpoint}?${query}`, { credentials: "include" });
      if (!res.ok) throw new Error((await res.json()).message || "Failed to fetch");
      return res.json();
    },
    enabled: !!activeBusiness?.id,
    staleTime: 0,
  });

  const exportPdf = () => {
    const url = `/api/reports/pl/${tab}/pdf?${query}`;
    window.open(url, "_blank");
  };

  const insightsQuery = useComparisonQuery ? query : "";
  const { data: insightsData } = useQuery<InsightsResponse>({
    queryKey: ["/api/reports/pl/insights", activeBusiness?.id, insightsQuery],
    queryFn: async () => {
      const res = await fetch(`/api/reports/pl/insights?${insightsQuery}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch insights");
      return res.json();
    },
    enabled: !!activeBusiness?.id && !!insightsQuery && !!data,
    staleTime: 0,
  });

  if (!activeBusiness) {
    return (
      <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 ml-64 p-6">Please select a business to view reports.</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 ml-64 p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Profit & Loss</h1>
            <p className="text-muted-foreground">{activeBusiness.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button onClick={exportPdf} disabled={!data}>
              <FileDown className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Report Period</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label htmlFor="startDate">Start Date</Label>
                <Input id="startDate" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="endDate">End Date</Label>
                <Input id="endDate" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>

              {tab !== "comparison" && (
                <div className="flex items-center gap-2 self-center pt-5">
                  <Switch id="compare" checked={compareEnabled} onCheckedChange={setCompareEnabled} />
                  <Label htmlFor="compare">Compare prior period</Label>
                </div>
              )}

              {(tab === "comparison" || compareEnabled) && (
                <>
                  <div>
                    <Label htmlFor="compareStart">Compare Start</Label>
                    <Input
                      id="compareStart"
                      type="date"
                      value={compareStartDate}
                      onChange={(e) => setCompareStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="compareEnd">Compare End</Label>
                    <Input
                      id="compareEnd"
                      type="date"
                      value={compareEndDate}
                      onChange={(e) => setCompareEndDate(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
          <TabsList>
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="comparison">Comparison</TabsTrigger>
            <TabsTrigger value="detailed">Detailed</TabsTrigger>
          </TabsList>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Profit & Loss Statement</CardTitle>
              {data && (
                <p className="text-sm text-muted-foreground">
                  {data.period.startDate} – {data.period.endDate}
                  {data.comparison && ` vs. ${data.comparison.period.startDate} – ${data.comparison.period.endDate}`}
                </p>
              )}
              {data && !data.validation.journalBalanced && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Journal out of balance: debits {fmtMoney(data.validation.debitTotal)} vs credits{" "}
                  {fmtMoney(data.validation.creditTotal)}
                </p>
              )}
            </CardHeader>
            <CardContent>
              {isLoading && <div className="text-muted-foreground">Loading…</div>}
              {isError && <div className="text-red-600">Failed to load report.</div>}
              {data && (
                <>
                  <TabsContent value="summary" forceMount={tab === "summary" ? true : undefined} hidden={tab !== "summary"}>
                    <SummaryTable data={data} />
                  </TabsContent>
                  <TabsContent
                    value="comparison"
                    forceMount={tab === "comparison" ? true : undefined}
                    hidden={tab !== "comparison"}
                  >
                    <ComparisonTable data={data} />
                  </TabsContent>
                  <TabsContent
                    value="detailed"
                    forceMount={tab === "detailed" ? true : undefined}
                    hidden={tab !== "detailed"}
                  >
                    <DetailedTable data={data} />
                  </TabsContent>
                </>
              )}
            </CardContent>
          </Card>
        </Tabs>

        {insightsData && <InsightsPanel data={insightsData} />}
      </div>
    </div>
  );
}
